/* Copyright (c) 2016-2023 Planticle Pty Ltd */

import ko from "knockout";
import birdata from "../../../api.js";
import googleMaps from '../../../util/google-maps.js';
import util from "../../../util/util.js";

import fieldManager from "../../../util/fields.js";
import mapStyles from '../../../map/styles.js';
import markerLayer from "../../../map/marker-layer.js";
import remoteMarkerLayer from '../../../map/remote-marker-layer.js';
import sharedSitesMap from "../../../map/shared-sites.js";
import DrawPolygonTool from "../../../util/draw-polygon-tool.js";

import h from "../../../util/dom-builder.js";

import "../../shared/location-search.js";

function ManageSharedSitesEditViewModel(params) {
	var self = this;

	self.loaded = ko.observable(false);
	self.map = ko.observable();
	self.mapOptions = ko.observable();

	self.layer = ko.observable();
	self.title = ko.pureComputed(function() {
		if(self.layer()) {
			return self.layer().name;
		}
	});

	self.mode = ko.observable("map"); // list, map

	self.selectedSite = ko.observable();

	self.selectedSiteExtra = ko.observable();

	self.sitesAtSelectedPoint = ko.observable(null);

	self.selectedSite.subscribe(site => {
		self.selectedSiteExtra(null);
		if(site) {
			if(site.new) {
				drawPolygonTool.showCancel = false;
				drawPolygonTool.drawPolygon();
			} else {
				birdata.sharedSite(site.id).then(self.selectedSiteExtra);
			}
		}
	});

	self.editingEnabled = ko.pureComputed(() => {
		return self.layer() && self.layer().editing_enabled;
	});

	self.surveyTypes = ko.observable([]);
	birdata.lookup("survey_type").then(self.surveyTypes);

	self.editMode = ko.observable("view"); // view, edit

	self.sharedSitesPromise = birdata.sharedSites({ layer_id: params.id });
	self.sharedSites = ko.observable();
	self.sharedSitesPromise.then(sites => {
		self.sharedSites(sites);
		sites.forEach(s => sitesById[s.id] = s);
	});

	self.showLocationSearch = ko.pureComputed(function() {
		return self.editMode() == "view";
	});

	self.locationResultSelected = function(suggestion) {
		var data = suggestion.data;
		var map = self.map();

		if(!map) {
			return;
		}

		switch(suggestion.type) {
			case 'LOCATION':
				map.fitBounds(new google.maps.LatLngBounds(
				  new google.maps.LatLng(data.ymin, data.xmin),
				  new google.maps.LatLng(data.ymax, data.xmax)));
				break;
			case 'SHARED_SITE':
				if(self.editMode() == 'view') {
					self.selectedSite(data);
				}
				map.fitBounds(googleMaps.bounds(data.bounds));
				break;
		}
	}

	var sharedSitePrivateUsers = ko.observable();
	var sitesById = {};
	var drawPolygonTool = new DrawPolygonTool();
	drawPolygonTool.polygonOptions = {
		strokeColor: 'red',
		fillOpacity: 0.1
	};
	drawPolygonTool.showRedraw = true;

	let pointSiteEditingMarker = null;

	function refreshPrivateUsers() {
		return birdata.sharedSitePrivateUsers(self.layer().code).then(sharedSitePrivateUsers);
	}

	birdata.sharedSiteLayers()
		.then(layers => layers.find(layer => layer.id == params.id))
		.then(layer => {
			self.layer(layer);
			self.loaded(true);
			refreshPrivateUsers();
		});

	// ----- Edit mode ------

	self.isSaving = ko.observable(true);
	self.saveError = ko.observable();

	self.beginEditing = function() {
		if(self.mode() != "map") {
			self.mode("map");
		}
		if(self.editingEnabled()) {
			self.polygonEditCount = drawPolygonTool.editCount;
			self.editMode("edit");
		}
	}

	self.createNewSite = function() {
		if(self.editingEnabled()) {
			self.selectedSite({ new: true, is_private: !!self.layer().force_sites_private });
			self.beginEditing();
		}
	}

	self.cancelEditing = function() {
		self.editMode("view");
		if(self.selectedSite().new) {
			self.selectedSite(null);
		}
	}

	self.saveChanges = function() {
		let isNew = !self.selectedSite().id;

		Promise.resolve().then(() => {
			let site = self.selectedSite();
			let json = {};

			if(site.id) {
				json.id = site.id;
			}

			// We only send the geometry if the polygon/point has been changed
			if(drawPolygonTool.editCount > self.polygonEditCount) {
				json.geom = {
					type: 'POLYGON',
					data: [ drawPolygonTool.encodedPolygon ]
				};
			}
			if(pointSiteEditingMarker != null && self.pointSiteEdited) {
				let p = pointSiteEditingMarker.getPosition();
				json.geom = {
					type: 'POINT',
					data: [ p.lng(), p.lat() ]
				};
			}

			self.siteFields().forEach(f => f.field.save(json));
			if(self.siteMetaFields().length) {
				json.metadata = site.metadata || {};
				self.siteMetaFields().forEach(f => f.field.save(json.metadata));
			}
			self.isSaving(true);
			self.saveError(null);
			drawPolygonTool.editingDisabled = true;

			if(site.id) {
				return birdata.updateSharedSite(json);
			} else {
				return birdata.createSharedSite(self.layer().code, json);
			}
		}).then((siteData) => {
			// Update client side copies of this site
			sitesById[siteData.id] = siteData;
			self.selectedSite(siteData);

			let updatedSites = self.sharedSites().map(s => s.id == siteData.id ? siteData : s);
			if(isNew) {
				updatedSites.push(siteData);
			}
			self.sharedSites(updatedSites);

			self.setupSharedSiteMarker(siteData);
			self.editMode("view");
		}).catch((error) => {
			console.log(error);

			let message = "Failed to save Shared Site";

			if(error && error.xhr) {
				try {
					message += ": " + JSON.parse(error.xhr.responseText).errors[0].message;
				} catch(e) {} // do nothing
			}

			self.saveError(message);
		}).finally(() => {
			drawPolygonTool.editingDisabled = false;
			self.isSaving(false);
		});
	}

	// TODO: extract this into utility module
	function confirm(title, message, cancelLabel, confirmLabel, callback) {
		var dialog = h('dialog', { $close: onClose }, [
			h('form', { 'method': 'dialog' }, [
				h('h1', title),
				h('p', message),
				h('div', { 'class': 'buttons' }, [
					h('button', { 'value': 'dismiss', 'class': 'button' }, cancelLabel),
					h('button', { 'value': 'confirm', 'class': 'button primary' }, confirmLabel)
				])
			].flat())
		]);

		function onClose() {
			dialog.parentElement.removeChild(dialog);

			if(callback) {
				callback(dialog.returnValue);
			}
		}

		// Container for styling
		var container = h('div', { 'class': 'birdata-embed' }, dialog);

		window.document.body.appendChild(container);
		dialog.showModal();
	}

	self.confirmDelete = function() {
		confirm('Delete site',
			'Are you sure you wish to delete the site ' + self.selectedSite().name + '?',
			'Cancel',
			'Delete',
			function(result) {
				if(result === 'confirm') {
					deleteSite();
				}
			});
	}

	function deleteSite() {
		let siteId = self.selectedSite().id;

		birdata.deleteSharedSite(siteId).then(function() {
			delete sitesById[siteId];
			self.selectedSite(null);
			self.sharedSites(self.sharedSites().filter(s => s.id != siteId));
			self.editMode("view");
		}).catch((error) => {
			console.log(error);
			alert("Delete site failed");
		});
	}

	// ------ Map ------

	googleMaps.load().then(function() {
		var center = new google.maps.LatLng(-22.50831, 136.078612);
		var zoom = 4;

		self.mapOptions(googleMaps.defaultOptions({
			center: center,
			zoom: zoom,
			fullscreenControl: false,
			mapTypeControl: false,
			zoomControl: false
		}));
	});

	ko.computed(function() {
		if(self.map() && self.loaded()) {
			setupMap(self.map());
		}
	});

	function zoomToSite(site) {
		if(self.map()) {
			if(site.bounds.minx == site.bounds.maxx && site.bounds.miny == site.bounds.maxy) {
				self.map().panTo(sitePosition(site));
				self.map().setZoom(18);
			} else {
				self.map().fitBounds(siteBounds(site));
			}
		}
	}

	function siteBounds(site, bounds) {
		return googleMaps.bounds(site.bounds, bounds);
	}

	function sitePosition(site) {
		return { lat: site.centroid.lat, lng: site.centroid.lon };
	}

	function markerMaxZoom() {
		return 11;
	}

	function setupMap(map) {
		if(setupMap.called) {
			return;
		} else {
			setupMap.called = true;
		}

		if(self.layer().bounds) {
			let layerBounds = googleMaps.bounds(self.layer().bounds);
			map.fitBounds(layerBounds, 10);
		}

		let hoverOverlay = googleMaps.hoverOverlay(map);
		let siteMarkersById = {};

		drawPolygonTool.map = map;
		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.mapTypeSelectorControl(map));
		map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(googleMaps.zoomControl(map));
		map.controls[google.maps.ControlPosition.RIGHT_TOP].push(googleMaps.coordinateControl(map));

		// Show polygon area
		var polygonAreaStatus = document.createElement("div");
		polygonAreaStatus.className = 'map-coordinate-control';
		map.controls[google.maps.ControlPosition.RIGHT_TOP].push(polygonAreaStatus);
		ko.computed(function() {
			var poly = drawPolygonTool.polygon();
			var editing = self.editMode() == 'edit';
			if(poly && editing) {
				let area = google.maps.geometry.spherical.computeArea(poly.getPath()) / 10000;
				polygonAreaStatus.textContent = "Polygon Area: " + area.toLocaleString() + " ha";
				polygonAreaStatus.classList.remove('hidden');
			} else {
				polygonAreaStatus.classList.add('hidden');
			}
		})

		let sharedSiteIcon = {
			path: google.maps.SymbolPath.CIRCLE,
			fillColor: 'purple',
			fillOpacity: 1,
			strokeWeight: 2,
			strokeColor: "white",
			scale: 6
		};

		// Polygon non-editing layer
		let sharedSiteLayer = sharedSitesMap.showLayer(map, {
			layerID: "sharedSites",
			outlineWidth: 6,
			minZoom: markerMaxZoom() + 1,
			featureColor: function(feature) {
				return 'purple';
			},
			polygonEvents: {
				click: function(evt) {
					if(self.editMode() != "edit") {
						let site = sitesById[this.userData.id];
						if(site) {
							self.selectedSite(site);
							map.panTo(sitePosition(site));
						}
					}
				},
				mousemove: function(evt) {
					hoverOverlay.position(evt.latLng);
				},
				mouseover: function(evt) {
					let site = sitesById[this.userData.id];
					if(site) {
						hoverOverlay.html(site.name);
						hoverOverlay.position(evt.latLng);
						hoverOverlay.show();
					}
				},
				mouseout: function(evt) {
					hoverOverlay.hide();
				}
			},
			markerEvents: {
				click: function(evt) {
					if(self.editMode() != "edit") {
						if(this.userData.length > 0) {
							let sites = this.userData.map(site => sitesById[site.id]).filter(x => x);
							let site = sites[0];
							if(site) {
								self.selectedSite(site);
								map.panTo(sitePosition(site));
							}
							if(sites.length > 1) {
								self.sitesAtSelectedPoint(sites);
							}
						}
					}
				},
				mousemove: function(evt) {
					hoverOverlay.position(evt.latLng);
				},
				mouseover: function(evt) {
					if(this.userData.length > 0) {
						let siteNames = this.userData
							.map(site => sitesById[site.id])
							.filter(x => x)
							.map(x => x.name)
							.join(', ');
						if(siteNames) {
							hoverOverlay.html(siteNames);
							hoverOverlay.position(evt.latLng);
							hoverOverlay.show();
						}
					}
				},
				mouseout: function(evt) {
					hoverOverlay.hide();
				}
			}
		});

		let surveyPointIcon = {
			path: google.maps.SymbolPath.CIRCLE,
			fillColor: "#008ac9",
			fillOpacity: 0.7,
			strokeColor: "#FFFFFF",
			strokeWeight: 2,
			strokeOpacity: 0.7,
			scale: 6
		};

		let surveyPointLayer = remoteMarkerLayer(map, {
			minZoom: 14,
			pointsForTile: function(tile) {
				return birdata.surveyTile({}, tile.x, tile.y, tile.z).then(function(points) {
					if(tile.removed) return;
					for(const p of points) {
						// Populate ID needed by marker layer
						p.id = p.lat + "," + p.lon;
					}
					return points;
				});
			},
			markerForPoint: function(p) {
				var title = util.formatQuantity(p.count, 'survey');

				var marker = new google.maps.Marker({
					position: new google.maps.LatLng(p.lat, p.lon),
					title: title,
					icon: surveyPointIcon,
					zIndex: 1
				});
				return marker;
			}
		});

		map.addListener("click", function(event) {
			if(self.editMode() != "edit") {
				self.selectedSite(null);
			}
		});

		sharedSiteLayer.setFeatureFilter(function(feature) {
			var site = feature.userData && feature.userData.attributes;
			if(self.selectedSite() && site.id == self.selectedSite().id && self.editMode() != 'view') {
				return false;
			}
			return site && site.layer == self.layer().code;
		});

		// Marker layer
		self.sharedSitesPromise.then(function(sharedSites) {
			var layer = markerLayer.layer(map, {
				density: 2,
				maxZoom: markerMaxZoom(),
				listeners: {
					click: function() {
						if(self.editMode() != "edit") {
							self.selectedSite(this.userData.sharedSite);
						}
					}
				}
			});

			function setupSharedSiteMarker(sharedSite) {
				var marker = new google.maps.Marker({
					position: { lat: sharedSite.centroid.lat, lng:sharedSite.centroid.lon },
					title: sharedSite.name,
					userData: {
						sharedSite
					},
					icon: sharedSiteIcon
				});
				siteMarkersById[sharedSite.id] = marker;
				layer.addMarker(marker);
			}
			sharedSites.forEach(setupSharedSiteMarker);

			self.setupSharedSiteMarker = setupSharedSiteMarker; // Store this so we can use it when we create a new site

			// Unfortunately this setTimeout seems to be needed to get the markers to reliably appear on first load
			// TODO: investigate
			setTimeout(function() {
				layer.refresh();
			});

			var infoWindow;

			ko.computed(function() {
				let site = self.selectedSite();
				let editing = (self.editMode() == "edit");

				if(infoWindow) {
					infoWindow.close();
					infoWindow = null;
				}
				if(site && !editing) {
					let anchor = siteMarkersById[site.id];
					if(anchor) {
						infoWindow = new google.maps.InfoWindow({
							content: site.name,
							position: anchor.getPosition(),
							pixelOffset: new google.maps.Size(0, -6)
						});
						infoWindow.open({ map });
					}
				}
			});
		});


		self.editMode.subscribe(mode => {
			if(mode == "edit") {
				var site = self.selectedSite();
				if(!site.new) {
					zoomToSite(site);
				}
			}
			sharedSiteLayer.refresh();
		});

		self.saveEnabled = ko.pureComputed(() => !drawPolygonTool.isDrawingPolygon());

		var instructionsControl = googleMaps.instructionsControl();
		instructionsControl.style.display = 'none';
		map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(instructionsControl);

		ko.computed(function() {
			if(self.editMode() == "edit") {
				if(self.selectedSiteExtra()) {
					var geom = self.selectedSiteExtra().geom;
					if(geom && geom.type == 'MULTIPOLYGON' && geom.data.length == 1 && geom.data[0].length == 1) {
						drawPolygonTool.encodedPolygon = geom.data[0][0];
					} else if(geom && geom.type == 'POLYGON' && geom.data.length == 1) {
						drawPolygonTool.encodedPolygon = geom.data[0];
					} else if(geom && geom.type == 'POINT') {
						pointSiteEditingMarker = new google.maps.Marker({
							position: { lat: geom.data[1], lng: geom.data[0] },
							zIndex: 1000,
							draggable: true,
							map: map,
							icon: {
								path: google.maps.SymbolPath.CIRCLE,
								fillColor: "red",
								fillOpacity: 1,
								strokeColor: "#FFFFFF",
								strokeWeight: 2,
								strokeOpacity: 1,
								scale: 7
							}
						});
						self.pointSiteEdited = false;
						pointSiteEditingMarker.addListener("dragend", function() {
							self.pointSiteEdited = true;
						});
						instructionsControl.textContent = 'Drag the red marker to change the site location.';
						instructionsControl.style.display = 'block';
					} else {
						alert("Unable to edit boundaries for this site");
					}
				}

			} else {
				drawPolygonTool.cancelPolygon();
				if(pointSiteEditingMarker) {
					pointSiteEditingMarker.setMap(null);
					pointSiteEditingMarker = null;
					instructionsControl.style.display = 'none';
				}
			}
		});
	}

	// -------- List --------

	self.tableParams = {
		tableClass: 'compact row-clickable',
		items: self.sharedSites,
		rowClick: function(sharedSite, evt) {
			if(evt.target.tagName == 'BUTTON') {
				self.mode("map");
				setTimeout(function() {
					self.selectedSite(sharedSite);
					zoomToSite(sharedSite);
				}, 100);
			}
		},
		columns: [{
			name: 'ID',
			sortable: true,
			value: (sharedSite) => sharedSite.source_ref,
			id: 'programs',
			style: { width: '8em' }
		}, {
			name: 'Name',
			sortable: true,
			value: (sharedSite) => sharedSite.name,
			id: 'name',
			style: {
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
				overflow: 'hidden'
			}
		}, {
			name: 'Users with access',
			sortable: true,
			value: (sharedSite) => {
				if(sharedSitePrivateUsers()) {
					var users = sharedSitePrivateUsers()[sharedSite.id];
					if(users && users.length > 0) {
						return users.map(u => u.full_name).join(", ");
					}
				}
				return "";
			},
			html: (sharedSite) => {
				if(sharedSitePrivateUsers()) {
					var users = sharedSitePrivateUsers()[sharedSite.id];
					if(users && users.length > 0) {
						return users.map(u => u.full_name).join(", ");
					} else {
						return "None";
					}
				} else {
					return "Loading…"
				}
			},
			id: 'users'
		}, {
			name: '',
			sortable: false,
			value: () => '',
			html: () => '<button class="button">View</button>',
			id: 'view',
			style: { width: '5em' }
		}],
		sortDescriptors: [{
			column: "name",
			ascending: "true"
		}],
		emptyMessage: "No shared sites to show.",
		pageSize: 100
	};


	// --------- Details ---------

	var fieldDefinitions = [{
		name: 'sourceRef',
		label: 'Shared Site ID',
		type: 'text',
		validate: 'required'
	}, {
		name: 'name',
		label: 'Shared Site Name',
		type: 'text',
		validate: 'required'
	}, {
		name: 'surveyType',
		label: 'Survey Type',
		type: 'options',
		validate: 'required',
		options: self.surveyTypes
	}, {
		name: 'isPrivate',
		help: 'Private sites are only visible to program administrators, and to individuals listed below under the "Users with access" section.',
		label: 'Private',
		type: 'boolean',
		disabled: () => self.layer().force_sites_private
	}];

	// ------- Layer-specific fields --------

	let bofProjectOptions = [
		'Victoria-wide',
		'Southern NSW',
		'Central-west New South Wales',
		'ACT-Yass region',
		'Western Australia',
		'Lockyer-Toowoomba'
	].map(function(x) {
		return { name: x, code: x };
	});

	let projectOptions = ko.observable([]);
	self.sharedSites.subscribe(function(sites) {
		updateProjectOptions();
	});
	function updateProjectOptions() {
		let sites = self.sharedSites() || [];

		let options = sites.map(s => s.metadata?.Project)
			.filter(x => !!x)
			.sort()
			.concat('Add new project…');

		options = util.uniq(options);

		projectOptions(options);
	}

	function metadataFieldDefinitions() {
		let layerCode = self.layer().code;
		if(layerCode == 'bof') {
			return [{
				name: 'Desc',
				key: 'Desc',
				label: 'Description',
				type: 'text',
				validate: 'required'
			}, {
				name: 'Project',
				key: 'Project',
				label: 'Project',
				type: 'options',
				options: bofProjectOptions,
				validate: 'required'
			}, {
				name: 'Property',
				key: 'Property',
				label: 'Property/Landowner',
				type: 'text',
				validate: 'required'
			}];
		} else if (layerCode == 'cnb') {
			return [{
				name: 'Project',
				key: 'Project',
				label: 'Project',
				type: 'text',
				options: projectOptions,
				otherOption: 'Add new project…'
			}];
		} else {
			return [];
		}
	}

	// -------- End Layer-specific fields --------

	self.siteFields = ko.observable([]);
	self.siteMetaFields = ko.observable([]);

	// Convenience to make fields easier to use in foreach in template
	self.siteFieldsWrapped = ko.pureComputed(() => {
		return self.siteFields().map(f => ({ value: f }));
	});
	self.siteMetaFieldsWrapped = ko.pureComputed(() => {
		return self.siteMetaFields().map(f =>({ value: f }));
	});

	self.selectedSite.subscribe(site => {
		if(site) {
			console.log(site);
			// General site fields
			let fields = fieldDefinitions.map(fieldManager.field);
			fields.forEach(f => f.field.load(site));
			self.siteFields(fields);

			// Layer-specific metadata fields
			let metaFields = metadataFieldDefinitions().map(fieldManager.field);
			if(site.metadata) {
				metaFields.forEach(f => f.field.load(site.metadata));
			}
			self.siteMetaFields(metaFields);
		} else {
			self.siteFields([]);
			self.siteMetaFields([]);
		}
	});

	// ------- Users ---------

	self.siteUsers = ko.pureComputed(() => {
		if(self.selectedSite()) {
			let site = self.selectedSite();
			if(sharedSitePrivateUsers()) {
				return sharedSitePrivateUsers()[site.id] || [];
			}
		}
	});

	self.siteUsersLoaded = ko.pureComputed(() =>
		!!sharedSitePrivateUsers());

	self.userToAdd = ko.observable();

	self.updatingPermissions = ko.observable(false);

	self.userToAdd.subscribe((user) => {
		if(user) {
			self.userToAdd(null);
			updateSitePermission(user.id, "add");
		}
	});

	self.removeUser = function(user) {
		confirm(
			'Remove access',
			`Remove site access for ${user.full_name}?`,
			'Cancel',
			'Remove access',
			function(result) {
				if(result === 'confirm') {
					updateSitePermission(user.id, "delete");
				}
			});
	}

	function updateSitePermission(userId, action) {
		self.updatingPermissions(true);
		birdata.updatePermission(userId, {
			action: action,
			role: "user",
			type: "shared_site",
			shared_site_id: self.selectedSite().id
		}).catch((e) => {
			console.log("Error while updating permissions");
			console.log(e);
			alert("Failed to " + action + " user");
		}).then(() => refreshPrivateUsers())
		.finally(() => {
			self.updatingPermissions(false);
		});
	}

	self.usersMatchingSearch = ko.observable([]);
	self.usersSearchText = ko.observable('').extend({ rateLimit: 400 });
	self.usersSearchText.subscribe(function(search) {
		updateUsers(search, self.usersSearchText, self.usersMatchingSearch);
	});
	function updateUsers(search, searchObs, matchesObs) {
		if(search) {
			birdata.users({q:search, full: true}).then(function(users) {
				if(searchObs() != search) return; // ignore stale request
				users = users.filter(u => u.full_name);
				matchesObs(users);
			});
		} else {
			matchesObs([]);
		}
	}
	self.userOptionText = function(user) {
		var text = user.full_name || "";
		if(user.email) {
			text = text + " <" + user.email + ">";
		}
		return text;
	};
}

ko.components.register('manage-shared-sites-edit', {
	viewModel: function(params) {
		return new ManageSharedSitesEditViewModel(params);
	},
	template: require('../../../../templates/admin/shared-sites/manage-shared-sites-edit.html')
});
